import React from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';

/**
 * Keeps a render error inside the campaign leads panel from blanking the whole app.
 *
 * The lead panel renders data straight from the provider and from years of stored leads,
 * so one malformed row should cost the panel, not the page — and the message has to name
 * the error, because a white screen tells a user (and a bug report) nothing.
 */

interface Props { children: React.ReactNode }
interface State { error: Error | null }

class CampaignPanelBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Campaign leads] render failed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Wrap>
        <Title>This panel hit an error</Title>
        <Message>{this.state.error.message || 'Unknown error'}</Message>
        <Hint>The rest of the page still works. Reloading usually clears it.</Hint>
        <Actions>
          <Btn type="button" onClick={() => this.setState({ error: null })}>Try again</Btn>
          <Btn type="button" $primary onClick={() => window.location.reload()}>Reload page</Btn>
        </Actions>
      </Wrap>
    );
  }
}

const Wrap = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 40px 24px; text-align: center;
`;

const Title = styled.span`
  font-family: NunitoSans; font-size: 15px; font-weight: 700; color: ${styles.colors.black600};
`;

const Message = styled.code`
  max-width: 640px; word-break: break-word;
  font-size: 12px; color: ${styles.colors.red600};
`;

const Hint = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
`;

const Actions = styled.div`display: flex; gap: 8px; margin-top: 10px;`;

const Btn = styled.button<{ $primary?: boolean }>`
  height: 34px; padding: 0 14px; border-radius: 8px; cursor: pointer;
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  border: 1px solid ${({ $primary }) => ($primary ? styles.colors.primary600 : styles.colors.black200)};
  background: ${({ $primary }) => ($primary ? styles.colors.primary600 : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : styles.colors.black500)};
`;

export default CampaignPanelBoundary;
